import { resolveDir as parent, resolvePath } from '../zip';
import { OpenXmlPart } from './openXmlPart';
import { getPartType } from './partRegistry';
import type { XlsxFile } from './xlsxFile';

export type Relationship = {
  id: string;
  type: string;
  target: string;
  absPath: string;
  /** Null if no associated file type supported */
  part: OpenXmlPart | null;
};

export class RelationshipPart extends OpenXmlPart {
  private relationships: {
    [id: string]: Relationship;
  } = {};

  constructor(partPath: string, originalXml: string, xlsxFile: XlsxFile) {
    super(partPath, originalXml, xlsxFile);

    const relationships = this.dom.getElementsByTagName('Relationship');
    for (const relationship of relationships) {
      const id = relationship.getAttribute('Id')!;
      const type = relationship.getAttribute('Type')!;
      const target = relationship.getAttribute('Target')!;
      const PartType = getPartType(type);
      const absPath = resolvePath(parent(parent(this.partPath)), target);

      let part = null;

      if (PartType) {
        const partContent = this.xlsxFile.getOriginalXml(absPath);
        if (partContent) {
          part = new PartType(absPath, partContent, this.xlsxFile);
        }
      }

      this.relationships[id] = {
        id,
        type,
        target,
        absPath,
        part,
      };
    }
  }

  override init(): void {
    for (const relationship of Object.values(this.relationships)) {
      relationship.part?.init();
    }
  }

  public static fromFile(
    filePath: string,
    xlsxFile: XlsxFile,
  ): RelationshipPart | null {
    const fileName = filePath.split('/').pop()!;
    const relFilePath = resolvePath(parent(filePath), `_rels/${fileName}.rels`);

    const originalXml = xlsxFile.getOriginalXml(relFilePath);
    if (originalXml) {
      return new RelationshipPart(relFilePath, originalXml, xlsxFile);
    }

    return null;
  }

  public getRelationship(id: string): Relationship | null {
    return this.relationships[id] ?? null;
  }

  public getPartFromElement<T extends OpenXmlPart>(element: Element): T | null {
    const relId = element.getAttribute('r:id')!;
    return (this.getRelationship(relId)?.part ?? null) as T | null;
  }
}
